// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { 
  ArrowRight, 
  Trophy, 
  Users, 
  Calendar,
  Target,
  Loader2
} from 'lucide-react';

interface SportsHubData {
  id: string;
  sport_id: string;
  thumbnail_image_url: string | null;
  hero_image_url: string | null;
  description: string | null;
  is_active: boolean;
  sport: {
    id: string;
    name: string;
    gender: string | null;
    active: boolean;
  };
}

const SportsHubsIndex: React.FC = () => {
  const [sportsHubs, setSportsHubs] = React.useState<SportsHubData[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchSportsHubs = async () => {
      try {
        const { data, error } = await supabase
          .from('sports_hub_content')
          .select(`
            id,
            sport_id,
            thumbnail_image_url,
            hero_image_url,
            description,
            is_active,
            sports!inner(
              id,
              name,
              gender,
              active
            )
          `)
          .eq('is_active', true)
          .order('sort_order');

        if (error) throw error;
        setSportsHubs((data || []).map(item => ({
          ...item,
          sport: Array.isArray(item.sports) ? item.sports[0] : item.sports
        })));
      } catch (err) {
        console.error('Error fetching sports hubs:', err);
        setError('Failed to load sports hubs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSportsHubs();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-secondary" />
            <span className="ml-3 text-lg font-semibold">Loading Sports Hubs...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-24">
            <h2 className="text-2xl font-black text-red-600 mb-4">Error Loading Sports</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Generate slug for sport
  const generateSportSlug = (sportName: string, gender?: string) => {
    let slug = sportName.toLowerCase().replace(/\s+/g, '-');
    if (gender && gender !== 'Co-Ed') {
      slug = `${gender.toLowerCase()}-${slug}`;
    }
    return slug;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="/lovable-uploads/740ce714-bc9c-4b38-82d6-f47503f2a68d.png"
            alt="Sports equipment background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
            SPORTS
            <span className="block text-secondary">HUBS</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Explore dedicated hubs for every sport. Connect with athletes, discover events, 
            and stay updated with the latest news and recruiting opportunities.
          </p>
        </div>
      </section>

      {/* Sports Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {sportsHubs.length === 0 ? (
            <div className="text-center py-16">
              <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-gray-600 mb-2">No Sports Available</h3>
              <p className="text-gray-500">Sports will be added soon. Check back later!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {sportsHubs.map((hub) => {
                const sportSlug = generateSportSlug(hub.sport.name, hub.sport.gender);
                
                return (
                  <Card key={hub.id} className="group hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden">
                    <CardContent className="p-0">
                      {/* Sport Image/Icon */}
                      <div className="h-48 bg-gradient-to-br from-slate-900 to-gray-900 relative overflow-hidden">
                        <div className="absolute inset-0 bg-black/20"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          {hub.thumbnail_image_url ? (
                            <img
                              src={hub.thumbnail_image_url}
                              alt={`${hub.sport.name} thumbnail`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Trophy className="w-16 h-16 text-secondary" />
                          )}
                        </div>
                        
                        {/* Gender Badge */}
                        {hub.sport.gender && hub.sport.gender !== 'Co-Ed' && (
                          <div className="absolute top-4 right-4">
                            <Badge className="bg-secondary text-black px-3 py-1 text-xs font-black">
                              {hub.sport.gender.toUpperCase()}
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Sport Info */}
                      <div className="p-6">
                        <h3 className="text-xl font-black text-slate-900 mb-3 group-hover:text-secondary transition-colors">
                          {hub.sport.name}
                        </h3>
                        
                        {hub.sport.gender && hub.sport.gender !== 'Co-Ed' && (
                          <p className="text-sm text-gray-600 mb-4">
                            {hub.sport.gender}'s
                          </p>
                        )}

                        {/* Action Button */}
                        <Link to={`/sports-hubs/${sportSlug}`}>
                          <Button className="w-full bg-slate-900 text-white hover:bg-slate-800 font-black group-hover:bg-secondary group-hover:text-slate-900 transition-all">
                            {hub.sport.name.toUpperCase()}
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SportsHubsIndex;